import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { LogInstanceToken } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import moment from 'moment';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { YesNoModalDialogComponent } from '../../../components/yes-no-modal-dialog/yes-no-modal-dialog.component';
import { YesNoModalDialogModel } from '../../../components/yes-no-modal-dialog/yes-no-modal-dialog.model';
import { ConfigConstants } from '../../../config/config-constants';
import { CONNECTION_ERROR_CODES } from '../../../enums/connection-error-codes.enum';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { ModalClass } from '../../../enums/modal-bootstrap-classes.enum';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { AppNotificationType } from '../../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../../services/app-notification/app-notification.service';
import { OdataService } from '../../../services/odata/odata.service';
import { StartWorkService } from '../../../services/timesheet/start-work.service';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import { NotificationUtilities } from '../../../utilities/notification-utilities';
import { TimeUtilities } from '../../../utilities/time-utilities';
import { persistNotificationsRequested, travelStatusRecorded } from '../../notification/actions/notification.actions';
import { NotificationState } from '../../notification/notification-collection.state';
import { selectNotificationEntryById } from '../../notification/selectors/notification.selector';
import {
    timesheetCollectionSaved,
    timesheetCollectionStorageSyncError,
    timesheetTravelBackEndAdjusted,
    updateStartOfWork,
} from '../../timesheet/actions/timesheet.actions';
import { LastEditableTravelBackEntry } from '../../timesheet/selectors/timesheet-merge.selector';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import {
    isStatusSkipped,
    isTravelOrOnSiteStatus,
} from '../../../services/travel-status-update/travel-status-update.rules';
import {
    submitTravelStatusRequested,
    travelStatusSubmitAborted,
    travelStatusSubmitFailed,
    travelStatusSubmitSucceeded,
} from '../actions/travel-status-update.actions';
import {
    etaSubRequest,
    requestToOutModel,
    travelStatusSubmissionFor,
    travSubRequest,
} from '../travel-status-update.converters';
import { selectTravelStatusSubmitContext, TravelStatusSubmitContext } from '../selectors/travel-status-update.selector';
import { ODataTravelStatusUpdateOutModel, TravelStatusUpdateRequest } from '../travel-status-update.types';
import { ODataObject } from '../../odata-types';

/**
 * Owns the travel-status-update orchestration that the legacy `TravelStatusUpdateService` used to
 * run inline: the modals, the router navigation, the app notifications, the debug logs and the
 * OData calls. This slice has no reducer and no feature state — it only reads the notification and
 * timesheet slices and writes back to them — so every step below is a side effect over those two
 * slices' actions and selectors, never over a state of its own.
 */
@Injectable()
export class TravelStatusUpdateEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly odataService = inject(OdataService);
    private readonly modalService = inject(BsModalService);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly appNotificationService = inject(AppNotificationService);
    private readonly log = inject(LogInstanceToken);
    private readonly notificationUtilities = inject(NotificationUtilities);
    // `StartWorkService` is itself an unmigrated, modal-heavy validation service (two warning
    // dialogs, an end-of-work-after-start-of-work check, an 11-hour break check) that has nothing
    // to do with travel status. It is injected here rather than reimplemented, which would
    // duplicate it; migrating it is a separate piece of work.
    private readonly startWorkService = inject(StartWorkService);

    /**
     * Runs the legacy `submitStatus` pipeline's five steps, in the legacy order, and resolves to
     * `travelStatusSubmitSucceeded` / `travelStatusSubmitFailed` / `travelStatusSubmitAborted`.
     * `concatMap`, not `mergeMap`: the component already guards concurrency with `exhaustMap`, and
     * serialising is the safe reading of a pipeline that writes to two slices.
     */
    public submitTravelStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitTravelStatusRequested),
            concatMap(({ request, correlationId }) => this.runSubmit(request, correlationId)),
        ),
    );

    /**
     * The `TravelStatusUpdateService: submitStatus: Status updated successfully to ${nextStatus}`
     * debug log, moved out of the pipeline: it reports a mutation, so it is a side effect of the
     * mutation rather than part of it. Correlated by `correlationId` rather than by reading
     * `nextStatus` off the outcome action (which does not carry it), so no shared mutable state is
     * needed to remember which request a given success belongs to.
     *
     * The inner pipe listens for all three outcomes and logs only the success, so that `take(1)`
     * fires on every request. Listening for the success alone would leave one inner subscription
     * alive forever per failed or aborted submit, since the outcome it waits for never arrives.
     */
    public logSubmitOutcome$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(submitTravelStatusRequested),
                mergeMap(({ request, correlationId }) =>
                    this.actions$.pipe(
                        ofType(travelStatusSubmitSucceeded, travelStatusSubmitFailed, travelStatusSubmitAborted),
                        filter((action) => action.correlationId === correlationId),
                        take(1),
                        tap((action) => {
                            if (action.type === travelStatusSubmitSucceeded.type) {
                                this.log.debug(
                                    `TravelStatusUpdateEffects: submitStatus: Status updated successfully to ${request.nextStatus}`,
                                );
                            }
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    private runSubmit(request: TravelStatusUpdateRequest, correlationId: string): Observable<Action> {
        return this.startOfWorkDependency$(request).pipe(
            concatMap(() => this.travelBackTimesheetDependency$(request)),
            concatMap(() => this.otherNotificationsPause$(request)),
            concatMap(() => this.skippedStatus$(request)),
            concatMap(() => this.saveTravelStatus$(request, correlationId)),
            catchError((error: unknown) =>
                of(
                    travelStatusSubmitFailed({
                        correlationId,
                        error: error instanceof Error ? error.message : String(error),
                    }),
                ),
            ),
        );
    }

    private readContext(notificationId: NotificationIdState): TravelStatusSubmitContext {
        return this.store.selectSignal(selectTravelStatusSubmitContext(notificationId))();
    }

    // --- Step 1: start-of-work dependency ---------------------------------------------------

    /**
     * Start of work needs to be set in the P58 environment. In AA countries, start of work is
     * replaced with a pre-defined work schedule and is always present; it is also not required for
     * PACE Online or wherever the timesheet feature is disabled.
     *
     * The `isNgmP58()` term the legacy guard also carried is dropped: the flag is always `true` in
     * every shipped configuration.
     */
    private startOfWorkDependency$(request: TravelStatusUpdateRequest): Observable<void> {
        const context = this.readContext(request.notificationId);
        if (!(
            isTravelOrOnSiteStatus(request.nextStatus) &&
            !context.isPaceOnlineUser &&
            context.isTimesheetFeatureEnabled
        )) {
            return of(undefined);
        }
        return this.setStartOfWorkForP58$(request, context);
    }

    /**
     * If start of work is invalid, sets it to the same timestamp as the TRAV/OSR request (or
     * directly, if the user does OSR directly). Asks the user to submit the pending timesheet if
     * start of work is set for an earlier day and the status is not TRAV.
     */
    private setStartOfWorkForP58$(
        request: TravelStatusUpdateRequest,
        context: TravelStatusSubmitContext,
    ): Observable<void> {
        const startOfWork = context.startOfWork;
        const requestStart = moment(request.start);

        if (!startOfWork.isValid()) {
            return this.startWorkService.setStartOfWork(requestStart);
        }
        if (!startOfWork.isSame(moment(), 'days') && context.currentStatusCode !== NOTIFICATION_STATUS_CODE.TRAV) {
            return this.submitPreviousTimesheet$().pipe(
                mergeMap(() => throwError(() => new Error(ConfigConstants.TravelStatusErrors.TIMESHEET_PENDING_ERROR))),
            );
        }
        return this.updateStartOfWork$(request, context, startOfWork);
    }

    /** Shows the "submit previous timesheet" modal; on confirmation, navigates to the timesheet page. */
    private submitPreviousTimesheet$(): Observable<void> {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_TITLE') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_BODY') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_TIMESHEET_BUTTON') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_OK_BUTTON') as string,
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (modalInstance.content?.modalResult ?? of(false)).pipe(
            take(1),
            mergeMap((modalResult: boolean) =>
                modalResult ? from(this.router.navigate([ConfigConstants.routeUrlTimesheet])) : of(undefined),
            ),
            catchError((error: unknown) => {
                this.log.error(
                    `TravelStatusUpdateEffects: submitPreviousTimesheet$: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                return of(undefined);
            }),
            tap(() => modalInstance.hide()),
            map(() => undefined),
        );
    }

    /**
     * Updates start-of-work if the request's start is before the current start of work and after
     * the previously submitted end of work. If the request's start is before the previous end of
     * work, the user has to set it correctly again.
     */
    private updateStartOfWork$(
        request: TravelStatusUpdateRequest,
        context: TravelStatusSubmitContext,
        startOfWork: moment.Moment,
    ): Observable<void> {
        const requestStart = moment(request.start);
        const endOfWork = context.submittedEndOfWork;

        if (!endOfWork.isValid() || requestStart.isSameOrAfter(endOfWork)) {
            if (requestStart.isBefore(startOfWork)) {
                this.log.debug(
                    `TravelStatusUpdateEffects: updateStartOfWork$: Updating start of work from ${startOfWork.format(
                        ConfigConstants.formatPatternDateTimeGeneric,
                    )} to ${requestStart.format(ConfigConstants.formatPatternDateTimeGeneric)}`,
                );
                // Awaited, not fire-and-forget: the legacy step went through
                // `TimesheetAndExpensesInterface.saveStartOfWork`, whose observable settled on the
                // persist outcome — so the rest of the pipeline ran only once the new start of work
                // was stored, and a storage failure failed the whole submit rather than being lost.
                return this.awaitTimesheetPersist$((correlationId) =>
                    updateStartOfWork({ startOfWork: requestStart.toISOString(), correlationId }),
                );
            }
            return of(undefined);
        }

        this.appNotificationService.setAppNotification(
            AppNotificationType.WARN,
            this.translate.instant('NOTIFICATION_STATUS_SUBMIT_EOW_PROBLEM_TITLE') as string,
            this.translate.instant('NOTIFICATION_STATUS_SUBMIT_EOW_PROBLEM_BODY', {
                endOfWork: endOfWork.format(ConfigConstants.formatPatternDateTimeGeneric),
            }) as string,
        );

        return throwError(
            () =>
                new Error(
                    `${ConfigConstants.TravelStatusErrors.INVALID_START_OF_WORK_ERROR} ${endOfWork.format(
                        ConfigConstants.formatPatternDateTimeGeneric,
                    )}`,
                ),
        );
    }

    // --- Step 2: travel-back timesheet dependency -------------------------------------------

    /**
     * Checks whether the last editable travel-back entry overlaps the requested TRAV/OSR time; if
     * it overlaps but starts after the leg, offers to shorten the leg for the user.
     */
    private travelBackTimesheetDependency$(request: TravelStatusUpdateRequest): Observable<void> {
        if (!isTravelOrOnSiteStatus(request.nextStatus)) {
            return of(undefined);
        }
        const context = this.readContext(request.notificationId);
        if (context.isPaceOnlineUser) {
            return of(undefined);
        }
        const lastTravelBackEntry = context.lastTravelBackEntry;
        if (!lastTravelBackEntry) {
            return of(undefined);
        }

        const requestStart = moment(request.start);
        const requestEnd = moment(request.end);
        const entryStart = moment(lastTravelBackEntry.start);
        const entryEnd = moment(lastTravelBackEntry.end);

        if (!TimeUtilities.hasOverlap(requestStart, requestEnd, entryStart, entryEnd)) {
            return of(undefined);
        }

        // If the TRAV/OSR time overlaps with the editable last travel-back entry but is before its
        // start, the entry must not be adapted and the user has to set TRAV/OSR correctly again.
        if (requestStart.isBefore(entryStart)) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                this.translate.instant('NOTIFICATION_STATUS_SUBMIT_OVERLAP_PROBLEM_TITLE') as string,
                this.translate.instant('NOTIFICATION_STATUS_SUBMIT_OVERLAP_PROBLEM_BODY', {
                    startTime: entryStart.format(ConfigConstants.formatPatternDateTimeGeneric),
                }) as string,
            );
            return throwError(() => new Error(ConfigConstants.TravelStatusErrors.TIMESHEET_OVERLAP_ERROR));
        }

        return this.adaptLastTravelBackTime$(request, lastTravelBackEntry, context.currentTimesheetId).pipe(
            mergeMap(() =>
                throwError(() => new Error(ConfigConstants.TravelStatusErrors.TIMESHEET_OVERLAP_ADAPT_LAST_TRBK_TIME)),
            ),
        );
    }

    /**
     * Shows the "adapt last travel-back time" modal; on confirmation, shortens the leg's end to the
     * requested start and awaits the timesheet persist outcome. The legacy `catchError` that
     * swallows a modal/save error and logs it is preserved.
     */
    private adaptLastTravelBackTime$(
        request: TravelStatusUpdateRequest,
        lastTravelBackEntry: LastEditableTravelBackEntry,
        currentTimesheetId: string | undefined,
    ): Observable<void> {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_TITLE') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_BODY') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_YES_BUTTON') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_CANCEL_BUTTON') as string,
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (modalInstance.content?.modalResult ?? of(false)).pipe(
            take(1),
            mergeMap((modalResult: boolean) => {
                if (!modalResult) {
                    return of(undefined);
                }
                if (!currentTimesheetId) {
                    // Contradictory state: the leg was read off the current timesheet, so there has
                    // to be one. Logged rather than swallowed — the user confirmed a change that
                    // cannot be written, and the pipeline still asks them to set TRAV/OSR again.
                    this.log.error(
                        `TravelStatusUpdateEffects: adaptLastTravelBackTime$: no current timesheet to adjust, entry ${lastTravelBackEntry.entryId} left unchanged`,
                    );
                    return of(undefined);
                }
                return this.awaitTimesheetPersist$((correlationId) =>
                    timesheetTravelBackEndAdjusted({
                        timesheetId: currentTimesheetId,
                        entryId: lastTravelBackEntry.entryId,
                        end: request.start,
                        correlationId,
                    }),
                );
            }),
            tap(() => modalInstance.hide()),
            catchError((error: unknown) => {
                this.log.error(
                    `TravelStatusUpdateEffects: adaptLastTravelBackTime$: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                return of(undefined);
            }),
        );
    }

    /**
     * Dispatches a timesheet write and completes once the timesheet slice has persisted it (or
     * errors with the persistence failure). The listener is set up before the dispatch, so a
     * synchronous outcome cannot be missed — the same pattern `TimesheetEffects.persistSliceAndAwait`
     * uses.
     *
     * Every write this effect makes to the timesheet goes through here, because both of them stood
     * behind a `TimesheetAndExpensesInterface` method whose observable settled on the persist
     * outcome. The action is built from the correlation id rather than passed in, so no caller can
     * dispatch one with an id this is not listening for.
     *
     * @param makeAction builds the write action from the correlation id to await it under
     */
    private awaitTimesheetPersist$(makeAction: (correlationId: string) => Action): Observable<void> {
        return new Observable<void>((subscriber) => {
            const correlationId = getUuidV4();
            const subscription = this.actions$
                .pipe(
                    ofType(timesheetCollectionSaved, timesheetCollectionStorageSyncError),
                    filter((action) => action.correlationId === correlationId),
                    take(1),
                )
                .subscribe((action) => {
                    if (action.type === timesheetCollectionStorageSyncError.type) {
                        subscriber.error(new Error(action.error));
                        return;
                    }
                    subscriber.next(undefined);
                    subscriber.complete();
                });
            this.store.dispatch(makeAction(correlationId));
            return () => subscription.unsubscribe();
        });
    }

    // --- Step 3: other notifications in progress ------------------------------------------

    /**
     * Warns the user when other notifications are in progress; on cancel, aborts the submit. On
     * confirm, nothing is written: pausing other notifications is already a documented no-op in
     * NgRx mode (`NotificationStatusNgrxService.savePausedIndicator`), so only the modal — the
     * user's chance to cancel — survives.
     */
    private otherNotificationsPause$(request: TravelStatusUpdateRequest): Observable<void> {
        if (!isTravelOrOnSiteStatus(request.nextStatus)) {
            return of(undefined);
        }
        const context = this.readContext(request.notificationId);
        if (context.otherInProgressIds.length === 0) {
            return of(undefined);
        }

        const descriptions = context.otherInProgressIds.map((id) => {
            const entry = this.store.selectSignal(selectNotificationEntryById(id))();
            return this.notificationUtilities.getNotificationTypeDescriptionFor(
                entry?.notificationType ?? ConfigConstants.NotificationTypes.Unknown,
                id.qmNum,
                id.maNum,
            );
        });

        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_TITLE') as string,
                    NotificationUtilities.getNotificationsFormattedWithNewLine(
                        descriptions,
                        this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_BODY') as string,
                    ),
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_OK_BUTTON') as string,
                    this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_CANCEL_BUTTON') as string,
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (modalInstance.content?.modalResult ?? of(false)).pipe(
            take(1),
            tap(() => modalInstance.hide()),
            mergeMap((modalResult: boolean) =>
                modalResult
                    ? of(undefined)
                    : throwError(
                          () =>
                              new Error(
                                  'TravelStatusUpdateEffects: otherNotificationsPause$: status change cancelled by user',
                              ),
                      ),
            ),
        );
    }

    // --- Step 4: skipped statuses -----------------------------------------------------------

    /**
     * PACE allows the user to skip a status: DISP -> TRAV/ROSR/FCC updates ETA first, DISP -> OSR
     * updates ETA then TRAV, and ETA/DURA -> OSR updates TRAV first.
     */
    private skippedStatus$(request: TravelStatusUpdateRequest): Observable<void> {
        const context = this.readContext(request.notificationId);
        if (!isStatusSkipped(context.currentStatusCode, request.nextStatus)) {
            return of(undefined);
        }

        this.log.debug(
            `TravelStatusUpdateEffects: skippedStatus$: Updating status from ${context.currentStatusCode} to ${request.nextStatus}`,
        );

        if (context.currentStatusCode === NOTIFICATION_STATUS_CODE.DISP) {
            return this.saveAndUpdateEta$(request, context).pipe(
                mergeMap(() =>
                    request.nextStatus === NOTIFICATION_STATUS_CODE.OSR
                        ? this.saveAndUpdateTrav$(request)
                        : of(undefined),
                ),
            );
        }
        return this.saveAndUpdateTrav$(request);
    }

    /** Saves the skipped ETA status: OData save, then records it on the notification entry. */
    private saveAndUpdateEta$(
        request: TravelStatusUpdateRequest,
        context: TravelStatusSubmitContext,
    ): Observable<void> {
        if (!context.notificationEntry) {
            return throwError(() => new Error('TravelStatusUpdateEffects: saveAndUpdateEta$: notification not found'));
        }
        return this.saveSubRequest$(
            etaSubRequest(request, context.plannedStart, context.plannedEnd),
            context.notificationEntry,
        );
    }

    /** Saves the skipped TRAV status: OData save, then records it on the notification entry. */
    private saveAndUpdateTrav$(request: TravelStatusUpdateRequest): Observable<void> {
        const context = this.readContext(request.notificationId);
        if (!context.notificationEntry) {
            return throwError(() => new Error('TravelStatusUpdateEffects: saveAndUpdateTrav$: notification not found'));
        }
        return this.saveSubRequest$(travSubRequest(request), context.notificationEntry);
    }

    /** OData save for a skipped-status sub-request, then records the status on the entry. Never persists on its own — the final `saveTravelStatus$` step persists everything at once. */
    private saveSubRequest$(subRequest: TravelStatusUpdateRequest, entry: NotificationState): Observable<void> {
        const outModel = requestToOutModel(subRequest, entry);
        const identifier = this.buildIdentifier(outModel, subRequest.notificationId);
        return this.odataService
            .deepInsertOutModelRequest(outModel as unknown as ODataObject, ENDPOINTS.StatusUpdate, identifier)
            .pipe(
                map(() => {
                    this.store.dispatch(
                        travelStatusRecorded({
                            id: subRequest.notificationId,
                            submission: travelStatusSubmissionFor(subRequest),
                        }),
                    );
                    return undefined;
                }),
            );
    }

    // --- Step 5: save the requested travel status -------------------------------------------

    /**
     * Saves the requested travel status to OData, records it on the notification entry and
     * persists the slice. On an OData failure, raises the offline/sync app notification and
     * resolves to `travelStatusSubmitAborted` — reproducing the legacy path where the failure was
     * caught, notified, and the pipeline completed silently.
     */
    private saveTravelStatus$(request: TravelStatusUpdateRequest, correlationId: string): Observable<Action> {
        const context = this.readContext(request.notificationId);
        if (!context.notificationEntry) {
            return throwError(() => new Error('TravelStatusUpdateEffects: saveTravelStatus$: notification not found'));
        }
        const outModel = requestToOutModel(request, context.notificationEntry);
        const identifier = this.buildIdentifier(outModel, request.notificationId);

        return this.odataService
            .deepInsertOutModelRequest(outModel as unknown as ODataObject, ENDPOINTS.StatusUpdate, identifier)
            .pipe(
                map(() => {
                    this.store.dispatch(
                        travelStatusRecorded({
                            id: request.notificationId,
                            submission: travelStatusSubmissionFor(request),
                        }),
                    );
                    this.store.dispatch(persistNotificationsRequested());
                    return travelStatusSubmitSucceeded({ correlationId });
                }),
                catchError((error: unknown) => {
                    this.log.error(
                        `TravelStatusUpdateEffects: saveTravelStatus$: ERROR -> ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? (this.translate.instant('OFFLINE_STORE_ERROR_TITLE') as string)
                            : (this.translate.instant('APP_SYNC_FAILED_TITLE') as string),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? (this.translate.instant('OFFLINE_STORE_ERROR_BODY') as string)
                            : (this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION') as string),
                    );
                    return of(travelStatusSubmitAborted({ correlationId }));
                }),
            );
    }

    /** The legacy deep-insert identifier shape: `${MessageType}-${identifier}-${uuid}`. */
    private buildIdentifier(outModel: ODataTravelStatusUpdateOutModel, notificationId: NotificationIdState): string {
        return `${outModel.MessageType}-${NotificationStateUtilities.notificationIdToString(notificationId)}-${getUuidV4()}`;
    }
}
